// @ts-check
// Passo "Conteúdo" do assistente da campanha de e-mail (spec/email.md seções 10.3, 10.5 e 19.7): sem conteúdo, a
// escolha entre um modelo da escola (copiado para a campanha, como no RD), um modelo pronto da galeria ou começar do zero
// num dos três modos; com conteúdo, o editor da frente 1C (EmailContentEditor, context CAMPAIGN), a origem do modelo com
// "Atualizar a partir do modelo", "Trocar o conteúdo", "Salvar como modelo", a prévia e o teste.
import { html } from 'htm/preact';
import { useState } from 'preact/hooks';
import { createEmptyEmailDocument } from '../../../../../shared/email-design/email-document.js';
import { applyTemplateToEmailCampaign } from '../../../data/email.js';
import { createEmailTemplate } from '../../../data/email-templates.js';
import { Button } from '../../../ui/button.js';
import { Icon } from '../../../ui/icon.js';
import { Modal } from '../../../ui/modal.js';
import { showErrorSnackbar, showSuccessSnackbar } from '../../../ui/snackbar.js';
import { EmailContentEditor } from '../../email-design/email-content-editor.js';
import { EmailDialog } from '../../email-design/email-dialog.js';
import { EmailGalleryGrid } from '../../email-design/template-create-dialog.js';
import { useEmailBranding } from '../../email-design/email-branding.js';
import { EmailTemplatePicker } from '../email-template-picker.js';
import { t } from '../../../i18n/index.js';

/**
 * @typedef {import('../../email-design/email-content-editor.js').EmailContentValue} EmailContentValue
 * @typedef {import('../../../data/email.js').EmailCampaign} EmailCampaign
 * @typedef {{ editorMode: any, document: any, subject: string, preheader: string, purpose: 'MARKETING' }} CampaignContent
 */

/**
 * Escolha do ponto de partida (sem conteúdo ainda).
 * @param {{ campaign: EmailCampaign, onReplaced: (campaign: EmailCampaign) => void, onStartContent: (content: CampaignContent) => void,
 *   returnPath: string, onCancel?: () => void }} props
 */
function ContentChooser({ campaign, onReplaced, onStartContent, returnPath, onCancel }) {
  const [panel, setPanel] = useState(/** @type {'TEMPLATES' | 'GALLERY' | null} */ (null));
  const [busyKey, setBusyKey] = useState(/** @type {string | null} */ (null));
  /** @param {string} templateId */
  const useTemplate = async (templateId) => {
    setBusyKey(templateId);
    try {
      onReplaced(await applyTemplateToEmailCampaign(campaign.id, templateId));
      setPanel(null);
    } catch (error) {
      showErrorSnackbar(error);
    } finally {
      setBusyKey(null);
    }
  };
  /** @param {import('../../../data/email-templates.js').EmailGalleryItem} item */
  const useGallery = (item) => {
    // o modelo pronto entra direto na campanha, sem criar um modelo na biblioteca da escola
    onStartContent({
      editorMode: item.editorMode, document: item.document, purpose: 'MARKETING',
      subject: campaign.subject.trim() ? campaign.subject : item.subject, preheader: campaign.preheader.trim() ? campaign.preheader : item.preheader,
    });
    setPanel(null);
  };
  /** @param {'BLOCKS' | 'RICH_TEXT' | 'HTML'} mode */
  const startFromScratch = (mode) => onStartContent({
    editorMode: mode, document: createEmptyEmailDocument(mode), subject: campaign.subject, preheader: campaign.preheader, purpose: 'MARKETING',
  });
  return html`<div class="ec-content-chooser" data-testid="email-campaign-content-chooser">
    <h3 class="ec-section-title">${t('emailCampaigns.content.chooseTitle')}</h3>
    <div class="ec-choice-grid">
      <button type="button" class="ec-choice-card" onClick=${() => setPanel('TEMPLATES')} data-testid="email-campaign-use-template">
        <${Icon} name="IconFileDescription" size=${22} />
        <strong>${t('emailCampaigns.content.myTemplates')}</strong>
        <span class="ec-muted">${t('emailCampaigns.content.myTemplatesHint')}</span>
      </button>
      <button type="button" class="ec-choice-card" onClick=${() => setPanel('GALLERY')}>
        <${Icon} name="IconLayoutGrid" size=${22} />
        <strong>${t('emailCampaigns.content.gallery')}</strong>
        <span class="ec-muted">${t('emailCampaigns.content.galleryHint')}</span>
      </button>
      <div class="ec-choice-card ec-choice-card-static">
        <${Icon} name="IconPencil" size=${22} />
        <strong>${t('emailCampaigns.content.fromScratch')}</strong>
        <div class="ec-choice-buttons">
          <${Button} title=${t('emailCampaigns.content.blocks')} size="small" variant="secondary" icon="IconLayoutRows" onClick=${() => startFromScratch('BLOCKS')} />
          <${Button} title=${t('emailCampaigns.content.richText')} size="small" variant="secondary" icon="IconTypography" onClick=${() => startFromScratch('RICH_TEXT')} />
          <${Button} title=${t('emailCampaigns.content.html')} size="small" variant="secondary" icon="IconCode" onClick=${() => startFromScratch('HTML')} />
        </div>
      </div>
    </div>
    ${onCancel ? html`<div class="ec-button-row"><${Button} title=${t('common.cancel')} size="small" variant="secondary" onClick=${onCancel} /></div>` : null}
    ${panel === 'TEMPLATES'
      ? html`<${EmailDialog} title=${t('emailCampaigns.content.myTemplates')} subtitle=${t('emailCampaigns.content.myTemplatesHint')} size="large" onClose=${() => setPanel(null)}>
          <${EmailTemplatePicker} value=${busyKey} returnPath=${returnPath} onChange=${(/** @type {{ templateId: string, purpose: string }} */ picked) => {
            if (picked.purpose === 'SERVICE') {
              showErrorSnackbar(new Error(t('emailCampaigns.content.serviceTemplate')));
              return;
            }
            useTemplate(picked.templateId);
          }} />
        <//>`
      : null}
    ${panel === 'GALLERY'
      ? html`<${EmailDialog} title=${t('emailCampaigns.content.gallery')} size="large" onClose=${() => setPanel(null)}>
          <${EmailGalleryGrid} busyKey=${null} onUse=${useGallery} />
        <//>`
      : null}
  </div>`;
}

/**
 * @param {{ campaign: EmailCampaign, content: CampaignContent | null, onContentChange: (value: CampaignContent, options?: { coalesceKey?: string }) => void,
 *   onStartContent: (content: CampaignContent) => void, onReplaced: (campaign: EmailCampaign) => void, templateChanged: boolean,
 *   onPreview: () => void, onTest: () => void, returnPath: string, readOnly?: boolean, extraProblems?: Array<{ code: string, blockId?: string }> }} props
 */
export function ContentStep({ campaign, content, onContentChange, onStartContent, onReplaced, templateChanged, onPreview, onTest, returnPath, readOnly = false, extraProblems = [] }) {
  const branding = useEmailBranding();
  const [isSwitching, setIsSwitching] = useState(false);
  const [confirm, setConfirm] = useState(/** @type {'switch' | 'update' | null} */ (null));
  const [isBusy, setIsBusy] = useState(false);

  if (!content?.document || isSwitching) {
    return html`<div class="ec-step" data-testid="email-campaign-content-step">
      <${ContentChooser} campaign=${campaign} returnPath=${returnPath} onCancel=${isSwitching ? () => setIsSwitching(false) : undefined}
        onReplaced=${(/** @type {EmailCampaign} */ replaced) => {
          setIsSwitching(false);
          onReplaced(replaced);
        }}
        onStartContent=${(/** @type {CampaignContent} */ started) => {
          setIsSwitching(false);
          onStartContent(started);
        }} />
    </div>`;
  }

  const updateFromTemplate = async () => {
    if (!campaign.sourceTemplateId) return;
    setIsBusy(true);
    try {
      onReplaced(await applyTemplateToEmailCampaign(campaign.id, campaign.sourceTemplateId));
      setConfirm(null);
    } catch (error) {
      showErrorSnackbar(error);
    } finally {
      setIsBusy(false);
    }
  };
  const saveAsTemplate = async () => {
    try {
      const template = await createEmailTemplate({
        name: campaign.name, editorMode: content.editorMode, document: content.document, subject: content.subject, preheader: content.preheader, purpose: 'MARKETING',
      });
      showSuccessSnackbar(t('emailCampaigns.content.savedAsTemplate', { name: template.name }));
    } catch (error) {
      showErrorSnackbar(error);
    }
  };

  return html`<div class="ec-step ec-content-step" data-testid="email-campaign-content-step">
    <div class="ec-content-toolbar">
      <span class="ec-muted">${campaign.sourceTemplateName ? t('emailCampaigns.content.fromTemplate', { name: templateNameOf(campaign) }) : null}</span>
      <div class="ec-button-row">
        <${Button} title=${t('emailCampaigns.actions.preview')} icon="IconEye" size="small" variant="secondary" onClick=${onPreview} />
        <${Button} title=${t('emailCampaigns.actions.sendTest')} icon="IconSend" size="small" variant="secondary" onClick=${onTest} />
        ${readOnly
          ? null
          : html`<${Button} title=${t('emailCampaigns.content.saveAsTemplate')} icon="IconDeviceFloppy" size="small" variant="secondary" onClick=${saveAsTemplate} />
              <${Button} title=${t('emailCampaigns.content.switchTemplate')} icon="IconSwitchHorizontal" size="small" variant="secondary" onClick=${() => setConfirm('switch')} />`}
      </div>
    </div>
    ${templateChanged && campaign.sourceTemplateId && !readOnly
      ? html`<div class="ec-notice" data-tone="blue" role="status">
          <${Icon} name="IconInfoCircle" size=${16} />
          <span>${t('emailCampaigns.content.templateChanged', { name: templateNameOf(campaign) })}</span>
          <${Button} title=${t('emailCampaigns.content.updateFromTemplate')} size="small" variant="secondary" onClick=${() => setConfirm('update')} />
        </div>`
      : null}
    <${EmailContentEditor} value=${content} context="CAMPAIGN" readOnly=${readOnly} brandColor=${branding.brandColor} logoUrl=${branding.logoUrl}
      settings=${branding.settings} institutionName=${branding.institutionName} extraProblems=${extraProblems}
      onChange=${(/** @type {CampaignContent} */ value, /** @type {{ coalesceKey?: string } | undefined} */ options) => onContentChange(value, options)} />
    ${confirm === 'switch'
      ? html`<${Modal} title=${t('emailCampaigns.content.switchTemplate')} subtitle=${t('emailCampaigns.content.switchConfirm')} confirmLabel=${t('emailCampaigns.content.switchTemplate')}
          onClose=${() => setConfirm(null)} onConfirm=${() => {
            setConfirm(null);
            setIsSwitching(true);
          }} />`
      : null}
    ${confirm === 'update'
      ? html`<${Modal} title=${t('emailCampaigns.content.updateFromTemplate')} subtitle=${t('emailCampaigns.content.updateConfirm')}
          confirmLabel=${t('emailCampaigns.content.updateFromTemplate')} isLoading=${isBusy} onClose=${() => setConfirm(null)} onConfirm=${updateFromTemplate} />`
      : null}
  </div>`;
}

/** Nome do modelo de origem (excluído depois da cópia: sem nome). @param {EmailCampaign} campaign */
const templateNameOf = (campaign) => campaign.sourceTemplateName ?? '';
